import { FC, useEffect, useState } from "react";

import { Box, CircularProgress, Typography } from "@mui/material";

import { getStaticDocument } from "../../api/apiService";

// Canonical names — must match the server-side whitelist
// (DocumentTemplateService::STATIC_PUBLIC_TEMPLATES).
export const STATIC_DOC_TERMS = "Terms and Conditions";
export const STATIC_DOC_PRIVACY = "Privacy Policy";
export const STATIC_DOC_ESIGN_CONSENT = "ESIGN consent";

interface StaticDocumentViewerProps {
  name: string;
}

/**
 * Fetches a read-only legal document (Terms / Privacy / ESIGN consent) from the
 * EMR and renders its HTML. Replaces the previously hardcoded Google Docs
 * iframes so the source of truth is the synced document_templates content.
 */
export const StaticDocumentViewer: FC<StaticDocumentViewerProps> = ({
  name,
}) => {
  const [html, setHtml] = useState<string | null>(null);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    let active = true;
    setHtml(null);
    setHasError(false);

    getStaticDocument(name)
      .then((res) => {
        if (!active) return;
        const docHtml = res?.data?.document?.html;
        if (docHtml) {
          setHtml(docHtml);
        } else {
          setHasError(true);
        }
      })
      .catch(() => {
        if (active) setHasError(true);
      });

    return () => {
      active = false;
    };
  }, [name]);

  if (hasError) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="body2">
          Unable to load this document right now. Please try again later.
        </Typography>
      </Box>
    );
  }

  if (html === null) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Render inside an iframe (srcDoc) to isolate the document's own CSS — the
  // synced Google-Docs HTML ships global element styles (ol, table td, ...)
  // that would otherwise bleed into the host app.
  return (
    <iframe
      title={name}
      srcDoc={html}
      style={{ width: "100%", height: "100%", border: "none" }}
    />
  );
};
